import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { configurationAPI } from "../../api-utility/configurationAPI";

// UPDATE SEQUENCE
export const updateSequence = createAsyncThunk(
  "configuration/updateSequence",
  async ({ slug, params, data }, { rejectWithValue }) => {
    try {
      const res = await configurationAPI.updateSequence(`tenant/${slug}/sequence/${params.type}`, data);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return { slug, data: res.data || res.data };
    } catch (err) {
      const server = err?.response?.data;
      return rejectWithValue({
        message: server?.message || "Failed to update sequence",
        data: server?.data || null,
      });
    }
  },
);

// FETCH ALL
export const fetchAll = createAsyncThunk(
  "configuration/fetchAll",
  async ({ slug, params }, { rejectWithValue }) => {
    try {
      const res = await configurationAPI.getAll(`tenant/${slug}`, params);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      const hasPagination = Array.isArray(res.data?.data?.data)
        ? res.data?.data?.data
        : res.data?.data || [];

      return {
        slug,
        params,
        data: hasPagination,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch records",
      );
    }
  },
);

// FETCH BY ID
export const fetchById = createAsyncThunk(
  "configuration/fetchById",
  async ({ slug, id }, { rejectWithValue }) => {
    try {
      const res = await configurationAPI.getById(`tenant/${slug}`, id);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return { slug, data: res.data?.data || res.data };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch record",
      );
    }
  },
);

// FETCH BY SLUG
export const fetchBySlug = createAsyncThunk(
  "configuration/fetchBySlug",
  async ({ slug, settingSlug }, { rejectWithValue }) => {
    try {
      const res = await configurationAPI.getAll(
        `tenant/${slug}/slug/${settingSlug}`,
      );
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return { slug, settingSlug, data: res.data?.data || res.data };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch setting",
      );
    }
  },
);

// CREATE
export const addItem = createAsyncThunk(
  "configuration/addItem",
  async ({ slug, data }, { rejectWithValue }) => {
    try {
      const res = await configurationAPI.add(`tenant/${slug}`, data);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return { slug, data: res.data || res.data };
    } catch (err) {
      const server = err?.response?.data;
      return rejectWithValue({
        message: server?.message || "Failed to create record",
        data: server?.data || null,
      });
    }
  },
);

// UPDATE
export const updateItem = createAsyncThunk(
  "configuration/updateItem",
  async ({ slug, id, data }, { rejectWithValue }) => {
    try {
      const res = await configurationAPI.update(`tenant/${slug}`, id, data);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return { slug, data: res.data || res.data };
    } catch (err) {
      const server = err?.response?.data;
      return rejectWithValue({
        message: server?.message || "Failed to update record",
        data: server?.data || null,
      });
    }
  },
);

// DELETE
export const deleteItem = createAsyncThunk(
  "configuration/deleteItem",
  async ({ slug, id }, { rejectWithValue }) => {
    try {
      const res = await configurationAPI.remove(`tenant/${slug}`, id);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return { slug, id, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete record",
      );
    }
  },
);

// CHANGE STATUS
export const changeStatus = createAsyncThunk(
  "configuration/changeStatus",
  async ({ slug, id, status }, { rejectWithValue }) => {
    try {
      const res = await configurationAPI.changeStatus(
        `tenant/${slug}`,
        id,
        status,
      );
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return {
        slug,
        id,
        status,
        message: res.data?.message || "Status updated successfully",
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update status",
      );
    }
  },
);

// SLICE
const configurationSlice = createSlice({
  name: "configuration",
  initialState: {
    entities: {},
    loading: false,
    loadingBySlug: {},
    error: null,
    message: null,
  },
  extraReducers: (builder) => {
    builder

      // FETCH ALL
      .addCase(fetchAll.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        const slug = action.meta.arg?.slug;
        if (slug) {
          state.loadingBySlug = { ...state.loadingBySlug, [slug]: true };
        }
      })
      .addCase(fetchAll.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload?.slug) {
          state.loadingBySlug[payload.slug] = false;
          state.entities[payload.slug] = payload.data;
        }
      })
      .addCase(fetchAll.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        const slug = action.meta.arg?.slug;
        if (slug) {
          state.loadingBySlug[slug] = false;
        }
      })
      // FETCH BY SLUG
      .addCase(fetchBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBySlug.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.entities[`${payload.slug}_${payload.settingSlug}`] = payload.data;
      })
      .addCase(fetchBySlug.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
  },
});

export default configurationSlice.reducer;
